import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ScrollText, ArrowLeft, Plus, Wrench, Sparkles, Gavel, Package } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ReleaseDate } from "@/components/ReleaseDate";
import { PublicationDialog } from "./admin/PublicationDialog";
import { useAppVersion } from "@/hooks/useAppVersion";

const SECTION_ICONS = {
  "Added": { icon: Plus, color: "text-emerald-300", ring: "border-emerald-300/40 bg-emerald-400/10" },
  "Improved": { icon: Sparkles, color: "text-cyan-300", ring: "border-cyan-300/40 bg-cyan-400/10" },
  "Fixed": { icon: Wrench, color: "text-amber-300", ring: "border-amber-300/40 bg-amber-400/10" },
  "Legal": { icon: Gavel, color: "text-fuchsia-300", ring: "border-fuchsia-300/40 bg-fuchsia-400/10" },
  "3D Prints": { icon: Package, color: "text-fuchsia-300", ring: "border-fuchsia-300/40 bg-fuchsia-400/10" },
};

/**
 * Public changelog — grouped release notes covering both Playden Classic and
 * the 3D Printing side. Content lives server-side (`/api/changelog`) so it
 * stays in sync with the current build.
 */
export default function ChangelogPage() {
  const { user } = useAuth();
  const version = useAppVersion();
  const { data, isLoading } = useQuery({
    queryKey: ["changelog", version],
    queryFn: async () => (await api.get("/changelog")).data,
    staleTime: 60_000,
  });

  const items = data?.items || [];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-14" data-testid="changelog-page">
      <div className="flex items-center gap-2 mb-3">
        <Link to="/home" data-testid="changelog-back-home" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.28em] text-white/45 hover:text-cyan-300 transition-colors">
          <ArrowLeft className="h-3 w-3" /> Back
        </Link>
      </div>
      <div className="flex items-center gap-2 mb-1">
        <ScrollText className="h-4 w-4 text-cyan-300" />
        <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-300">Playden Updates</span>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[0.05em] text-white leading-[1.05]" data-testid="changelog-title">
        PATCH NOTES
      </h1>
      <p className="mt-2 text-white/60 text-sm sm:text-base">
        Updates for Playden Classic and the 3D Printing Studio.
        Current build:{" "}
        <span className="font-mono text-cyan-300" data-testid="changelog-current-version">V {data?.current_version || "…"}</span>{" "}
        · <span className="font-mono text-white/60" data-testid="changelog-revision-count">{data?.revision_count ?? "—"} recorded updates</span>
      </p>
      <div className="mt-3 rounded-lg border border-cyan-300/25 bg-cyan-400/[0.05] p-3 text-[13px] text-white/75" data-testid="changelog-roadmap-cta">
        Have an idea? See what&apos;s coming next and propose your own on the <Link to="/roadmap" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-4 font-semibold">public roadmap →</Link>
      </div>

      {isLoading ? (
        <div className="mt-10 text-white/50 text-sm" data-testid="changelog-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-white/10 bg-[hsl(222_22%_7%)]/80 p-8 text-center text-white/60" data-testid="changelog-empty">
          No release notes yet — check back after the next update.
        </div>
      ) : (
        <div className="mt-8 space-y-5" data-testid="changelog-list">
          {items.map((entry) => (
            <VersionCard key={entry.version} entry={entry} isAdmin={user?.role === "admin"} />
          ))}
        </div>
      )}

      <div className="mt-10 border-t border-white/10 pt-4">
        <Link to="/terms" data-testid="changelog-view-terms" className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.28em] text-white/50 hover:text-cyan-300 transition-colors">
          <Gavel className="h-3 w-3" /> Terms of Service
        </Link>
      </div>
    </div>
  );
}

function VersionCard({ entry, isAdmin }) {
  return (
    <div
      className="rounded-2xl border border-white/10 bg-[hsl(222_22%_7%)]/80 p-4 sm:p-5"
      data-testid={`changelog-entry-${entry.version}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
        <div className="font-display text-xl sm:text-2xl text-white tracking-[0.05em]" data-testid={`changelog-version-${entry.version}`}>
          V <span className="text-cyan-300">{entry.version}</span>
        </div>
        <ReleaseDate entry={entry} testId={`changelog-date-${entry.version}`} />
      </div>
      <div className="space-y-3">
        {Object.entries(entry.sections || {}).map(([label, bullets]) => {
          const cfg = SECTION_ICONS[label] || SECTION_ICONS["Added"];
          const Icon = cfg.icon;
          return (
            <div key={label}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.22em] ${cfg.ring} ${cfg.color}`}>
                  <Icon className="h-3 w-3" /> {label}
                </div>
              </div>
              <ul className="space-y-1.5">
                {bullets.map((b, i) => (
                  <li key={b} className="pl-4 text-sm text-white/75 leading-relaxed relative">
                    <span className="absolute left-0 top-2 h-1 w-1 rounded-full bg-white/30" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      {isAdmin && <div className="mt-3"><PublicationDialog entry={entry} /></div>}
    </div>
  );
}
